import type { LevelConfig, LevelPiece } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as
// diamond_tower_showcase.ts's local COMMON_DIFFICULTY.
const COMMON_DIFFICULTY = 0;

const TILT_DEG = 15;
const COLS = 6;
const ROWS = 6;
// How many blocks deep the wall is. Depth runs along the tilt's axis of
// rotation, so every layer sits at the same height on the slope.
const LAYERS = 3;

/**
 * Static test level for TableConfig.tiltDeg and .liftFrom (see level_config.ts,
 * PhysicsTable#applyTilt and GameScreen#tableLifts): two tabletops tipped
 * TILT_DEG degrees in OPPOSITE directions, each carried by two legs of
 * different lengths instead of the usual single central post. The back one
 * stands on longer legs so its downhill edge starts where the front one's
 * uphill edge finishes — the pair reads as one continuous zig-zag.
 *
 * What to look for, since four separate things have to agree:
 *   - the tabletop art is tipped, and a leg meets it at EACH end (the
 *     downhill one short, the uphill one tall);
 *   - the blocks stand perpendicular to the surface, not upright — the whole
 *     authored-flat layout is rotated as one piece about the tabletop's
 *     centre (GameScreen#createLayeredBlocks);
 *   - nothing slides off or disappears at the downhill end: the tabletop's
 *     collider carries the tilt as its own rotation, and PhysicsTable's
 *     isOutside() measures against the slope rather than one flat height;
 *   - the two slopes MEET: the back table's low edge sits at the height of
 *     the front table's high edge, with all four feet still on the ground.
 */
// mirrored = false: the staircase's tall side is on the left (col 0);
// mirrored = true: the same staircase flipped so its tall side is on the right.
function makeWall( mirrored: boolean ): { grid: number[][]; pieces: LevelPiece[] }
{
  const grid: number[][] = [];
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let row = 0; row < ROWS; row++ )
  {
    const grid_row: number[] = new Array( COLS ).fill( 0 );

    for ( let step = 0; step < COLS - row; step++ )
    {
      const col = mirrored ? COLS - 1 - step : step;
      grid_row[col] = id;
      // `row` counts up from the tabletop, while y and grid rows count down from the top.
      pieces.push( { id, type: 9, x: col, y: ROWS - 1 - row, width: 1, height: 1 } );
      id++;
    }

    grid.unshift( grid_row );
  }

  return { grid, pieces };
}

function makeTableLayers( mirrored: boolean )
{
  return Array.from( { length: LAYERS }, () => makeWall( mirrored ) );
}

const TILTED_TABLE_LEVEL: LevelConfig = {
  level: 90304,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  tables: [
    // Downhill to the right: its high edge is on the left.
    {
      layers: makeTableLayers( false ),
      tiltDeg: TILT_DEG,
      slot: { u: 0, v: 0 },
      shape: "oval",
    },
    // Tipped the other way, so its LOW edge is on the left too — which is the
    // edge liftFrom raises onto the front table's high one.
    {
      // Mirrored, so its tall side is on its own uphill edge, like the front one's.
      layers: makeTableLayers( true ),
      tiltDeg: -TILT_DEG,
      slot: { u: 0, v: -1 },
      liftFrom: 0,
    },
  ],
};

export class TiltedTableShowcase
{
  public static readonly LEVEL = TILTED_TABLE_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? TILTED_TABLE_LEVEL : null;
  }
}
